import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, filter, map, Subject, switchMap, timer } from "rxjs";
import {
  Bet,
  BookmakerMarket,
  BookmakerRunner,
  CalculationBet,
  FancyMarket,
  FancyRunner,
  Market,
  MarketProvider,
  MarketResponse,
  PredictedBet,
  Runner,
  Side,
} from "../types/bet.type";
import { BetPlacementService } from "./bet-placement.service";
import { BalanceData } from "../types/balance-data.type";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { DebuggingService } from "../../../../desktop/src/app/components/debugging-modal/services/debugging.service";
import { debounceTime, takeUntil } from "rxjs/operators";
import { AsianHandicapService } from "./asian-handicap.service";
import { RiskExposureService, Bet as RiskBet } from "./risk-exposure.service";

// Extend Window interface to include gc method
declare global {
  interface Window {
    gc?: () => void;
  }
}

const MARKET_URL = environment.mURL;
const LADDER_RANGE_LIMIT = 2;
const CLEANUP_INTERVAL = 1 * 60 * 1000; // 1 minute
const DEBOUNCE_TIME = 200; // 1 second debounce for bet updates

@Injectable({
  providedIn: "root",
})
export class BetService implements OnDestroy {
  predictedBets$ = new BehaviorSubject<Record<string, PredictedBet>>({}); // Holds bet information to be shown as prediction

  matchedBets$ = new BehaviorSubject<Bet[]>([]); // Bets with stakeRemaining = 0 and stakeMatched > 0

  unmatchedBets$ = new BehaviorSubject<Bet[]>([]); // Bets with stakeRemaining > 0 and stakeMatched = 0

  partiallyMatchedBets$ = new BehaviorSubject<Bet[]>([]); // // Bets with stakeRemaining > 0 and stakeMatched > 0

  pendingBets$ = new BehaviorSubject<Bet[]>([]); // Bets with PENDING status

  nonPendingBets$ = new BehaviorSubject<Bet[]>([]); // Bets with status different from PENDING

  betsMarkets$ = new BehaviorSubject<Record<string, Market>>({}); // Holds the market for each unmatched bets

  matchedNonPendingBets$ = new BehaviorSubject<Bet[]>([]); // Bets with status different from PENDING, stakeRemaining = 0 and stakeMatched > 0

  unmatchedNonPendingBets$ = new BehaviorSubject<Bet[]>([]); // Bets with status different from PENDING, stakeRemaining > 0 and stakeMatched = 0

  lapsedNonPendingBets$ = new BehaviorSubject<Bet[]>([]); // // Bets with status different from PENDING, stakeRemaining = 0 and stakeMatched = 0 and sizeLapsed > 0

  partiallyMatchedNonPendingBets$ = new BehaviorSubject<Bet[]>([]); // // Bets with status different from PENDING, stakeRemaining > 0 and stakeMatched > 0

  unmatchedBets: Bet[] = [];

  private destroy$ = new Subject<void>();
  private cleanupTimer$ = timer(CLEANUP_INTERVAL, CLEANUP_INTERVAL);

  constructor(
    private readonly betPlacementService: BetPlacementService,
    private readonly httpClient: HttpClient,
    private readonly debuggingService: DebuggingService,
    private readonly asianHandicapService: AsianHandicapService,
    private readonly riskExposureService: RiskExposureService,
  ) {
    this.betPlacementService.openBetsSubject.pipe(takeUntil(this.destroy$), debounceTime(DEBOUNCE_TIME)).subscribe({
      next: (bets: Bet[]) => {
        const matchedNonPendingBets: Bet[] = [];
        const unmatchedNonPendingBets: Bet[] = [];
        const partiallyMatchedNonPendingBets: Bet[] = [];

        // If SIZE_REMAINING = 0 => Show MATCHED
        // If SIZE_MATCHED = 0 => Show UNMATCHED
        // If SIZE_MATCHED > 0 and SIZE_REMAINING > 0 => Show Partially Matched
        const nonPendingBets: Bet[] = bets.filter((bet) => bet.status !== "PENDING");
        const pendingBets: Bet[] = bets.filter((bet) => bet.status === "PENDING");
        nonPendingBets.forEach((bet) => {
          if (Number(bet.stakeRemaining) === 0 && Number(bet.stakeMatched) > 0) {
            matchedNonPendingBets.push(bet);
          } else if (Number(bet.stakeMatched) === 0 && Number(bet.stakeRemaining) > 0) {
            unmatchedNonPendingBets.push(bet);
            if (!this.unmatchedBets.some((existingBet) => existingBet.betId === bet.betId)) {
              this.unmatchedBets.push(bet);
            }
          } else if (Number(bet.stakeRemaining) > 0 && Number(bet.stakeMatched) > 0) {
            partiallyMatchedNonPendingBets.push(bet);
          }
        });

        this.matchedNonPendingBets$.next(matchedNonPendingBets);
        this.unmatchedNonPendingBets$.next(unmatchedNonPendingBets);
        this.partiallyMatchedNonPendingBets$.next(partiallyMatchedNonPendingBets);
        this.nonPendingBets$.next(nonPendingBets);
        this.pendingBets$.next(pendingBets);

        const matchedBets: Bet[] = [];
        const unmatchedBets: Bet[] = [];
        const partiallyMatchedBets: Bet[] = [];
        bets.forEach((bet) => {
          if (Number(bet.stakeRemaining) === 0 && Number(bet.stakeMatched) > 0) {
            matchedBets.push(bet);
          } else if (Number(bet.stakeMatched) === 0 && Number(bet.stakeRemaining) > 0) {
            unmatchedBets.push(bet);
          } else if (Number(bet.stakeRemaining) > 0 && Number(bet.stakeMatched) > 0) {
            partiallyMatchedBets.push(bet);
          }
        });

        this.matchedBets$.next(matchedBets);
        this.unmatchedBets$.next(unmatchedBets);
        this.partiallyMatchedBets$.next(partiallyMatchedBets);
      },
    });

    // Setup periodic cleanup
    this.cleanupTimer$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.performMemoryCleanup();
    });

    // Prepare market details
    this.collectBetMarkets();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    // Clear all BehaviorSubjects to free memory
    this.predictedBets$.next({});
    this.matchedBets$.next([]);
    this.unmatchedBets$.next([]);
    this.partiallyMatchedBets$.next([]);
    this.pendingBets$.next([]);
    this.nonPendingBets$.next([]);
    this.betsMarkets$.next({});
    this.matchedNonPendingBets$.next([]);
    this.unmatchedNonPendingBets$.next([]);
    this.lapsedNonPendingBets$.next([]);
    this.partiallyMatchedNonPendingBets$.next([]);
  }

  calculatePotentialProfit(size: number, price: number, side: Side, sizeCancelled: number, priceMatched: number, bettingType: string) {
    // WHEN BETTING_TYPE = 'LINE' THEN size
    if (bettingType === "LINE") {
      return size;
    } else {
      // WHEN bf_size = 0 AND side = 'BACK' THEN round(size * (price - 1), 2)
      if (size === 0 && side === "BACK") {
        return Number((size * (price - 1)).toFixed(2));
      }
      // WHEN bf_size = 0 AND side = 'LAY' THEN size
      else if (size === 0 && side === "LAY") {
        return size;
      }
      // WHEN bf_size > 0 AND side = 'BACK' THEN round(size  (1 - size_cancelled / NULLIF(bf_size, 0))(CASE WHEN price_matched IS NOT NULL AND price_matched > 0 THEN price_matchedELSE priceEND - 1),2)
      else if (size > 0 && side === "BACK") {
        return Number((size * (1 - sizeCancelled / size) * ((priceMatched > 0 ? priceMatched : price) - 1)).toFixed(2));
      }
      // WHEN bf_size > 0 AND side = 'LAY' THEN round(size * (1 - size_cancelled / NULLIF(bf_size, 0)), 2)
      else if (size > 0 && side === "LAY") {
        return Number((size * (1 - sizeCancelled / size)).toFixed(2));
      }
      // ELSE 0
      else {
        return 0;
      }
    }
  }

  calculatePotentialLoss(size: number, price: number, side: Side, sizeCancelled: number, priceMatched: number, bettingType: string) {
    // WHEN BETTING_TYPE = 'LINE' THEN size
    if (bettingType === "LINE") {
      return size;
    } else {
      // WHEN bf_size = 0 AND side = 'LAY' THEN round(size * (price - 1), 2)
      if (size === 0 && side === "LAY") {
        return Number((size * (price - 1)).toFixed(2));
      }
      // WHEN bf_size = 0 AND side = 'BACK' THEN size
      else if (size === 0 && side === "BACK") {
        return size;
      }
      // WHEN bf_size > 0 AND side = 'LAY' THEN round(size  (1 - size_cancelled / NULLIF(bf_size, 0))(CASE WHEN price_matched IS NOT NULL AND price_matched > 0 THEN price_matchedELSE priceEND - 1),2)
      else if (size > 0 && side === "LAY") {
        return Number((size * (1 - sizeCancelled / size) * ((priceMatched > 0 ? priceMatched : price) - 1)).toFixed(2));
      }
      // WHEN bf_size > 0 AND side = 'BACK' THEN round(size * (1 - size_cancelled / NULLIF(bf_size, 0)), 2)
      else if (size > 0 && side === "BACK") {
        return Number((size * (1 - sizeCancelled / size)).toFixed(2));
      }
      // ELSE 0
      else {
        return 0;
      }
    }
  }

  /**
   * Get the details of the specified markets
   * @param marketIds
   */
  getDetailsForMultipleNonPendingBfMarkets(marketIds: string[]) {
    return this.httpClient
      .get<MarketResponse<Market[] | Market>>(`${MARKET_URL}/bf/market_status`, {
        params: {
          ids: marketIds.join(","),
        },
      })
      .pipe(map((response) => response.data));
  }

  /**
   * Get the details of the specified pending markets
   * @param marketIds
   */
  getDetailsFoPendingBfMarkets(marketIds: string[]) {
    return this.httpClient.post<MarketResponse<Market[]>>(`${MARKET_URL}/bf/markets`, { marketIds }).pipe(map((response) => response.data));
  }

  /**
   * Check if the market is a line market or not
   */
  isLineMarket(market: Market): boolean {
    return market.description?.["bettingType"] && new RegExp(/LINE/, "ig").test(market.description["bettingType"]) && !this.isAsianHandicap(market);
  }

  /**
   * Checks if the given market is categorized as an Asian Handicap betting type.
   *
   * @param {Market} market - The market object to evaluate, containing descriptions and properties related to the betting market.
   * @return {boolean} True if the market's betting type matches "ASIAN_HANDICAP", false otherwise.
   */
  isAsianHandicap(market: Market): boolean {
    return market.description?.["bettingType"] && new RegExp(/ASIAN_HANDICAP/, "ig").test(market.description["bettingType"]);
  }

  getBetsOnlyWithinMarket(bets: CalculationBet[], marketId: string) {
    return bets.filter((bet) => bet.marketGroupId === marketId);
  }

  calculateMarketBasedExposure(bets: CalculationBet[], marketGroupId: string): number {
    // Filter bets only within that market
    const betsOnlyWithinMarket = this.getBetsOnlyWithinMarket(bets, marketGroupId);

    // Get the list of runners withing the market
    const market = this.betsMarkets$.getValue()[betsOnlyWithinMarket[0]?.marketId];
    const runners = market?.runners;

    // Calculate exposures for each runner and take minimum
    if (runners && runners.length > 0) {
      const marketExposures = runners.map((runner) => this.calculateRunnerProfitOrLoss(betsOnlyWithinMarket, runner.selectionId, this.isLineMarket(market)));

      return Math.abs(Math.min(...marketExposures, 0));
    }

    return 0;
  }

  /**
   * Calculate the profit or loss value
   * @param bets
   * @param selectionId
   * @param isLineMarket
   */
  calculateRunnerProfitOrLoss(bets: CalculationBet[], selectionId: number, isLineMarket: boolean) {
    if (isLineMarket) {
      // This is needed because for Fancy markets, selection ID is what makes them unique. This also works for BF LINE markets
      const betForTheRunner = bets.filter((bet) => bet.selectionId === selectionId);
      return Math.min(
        ...this.calculateLadderRangesAndValues(betForTheRunner.map((bet) => ({ ...bet, stake: Number(bet.betSize), rangeOdd: bet.dfOdds }))).values,
      );
    }

    const mySelectionBets = bets.filter((bet) => bet.selectionId === selectionId);
    const otherSelectionsBets = bets.filter((bet) => bet.selectionId !== selectionId);

    if (mySelectionBets.length === 0 && otherSelectionsBets.length === 0) {
      return 0;
    }

    const currentSelectionProfits: number[] = [];
    mySelectionBets.forEach((bet) => {
      if (bet.side === "BACK") {
        currentSelectionProfits.push(Number(bet.potentialProfit));
      }
    });
    otherSelectionsBets.forEach((bet) => {
      if (bet.side === "LAY") {
        currentSelectionProfits.push(Number(bet.potentialProfit));
      }
    });

    const currentSelectionLosses: number[] = [];
    mySelectionBets.forEach((bet) => {
      if (bet.side === "LAY") {
        currentSelectionLosses.push(Number(bet.potentialLoss));
      }
    });
    otherSelectionsBets.forEach((bet) => {
      if (bet.side === "BACK") {
        currentSelectionLosses.push(Number(bet.potentialLoss));
      }
    });

    const mySelectionProfit = currentSelectionProfits.reduce((prev, current) => prev + current, 0);
    const mySelectionLoss = currentSelectionLosses.reduce((prev, current) => prev + current, 0);

    return mySelectionProfit - mySelectionLoss;
  }

  calculateRunnerBasedExposure(bets: CalculationBet[], marketIdSelectionIdConcatenated: string): number {
    const betsOnlyWithinRunner = bets
      .filter(
        (bet) => bet.marketId === marketIdSelectionIdConcatenated.split("-")[0] && bet.selectionId === Number(marketIdSelectionIdConcatenated.split("-")[1]),
      )
      .map((bet) => ({
        ...bet,
        side: bet.provider === MarketProvider.BF ? bet.side : bet.side === "BACK" ? "LAY" : "BACK",
        stake: Number(bet.betSize),
      }));
    const ladderValues = this.calculateLadderRangesAndValues(betsOnlyWithinRunner).values;

    return Math.abs(Math.min(0, ...ladderValues));
  }

  getMarketSelectionIds(betModels: any[]): Map<string, string[]> {
    // identify unique market ids
    // for each market id identify the selection id
    // let marketSelectionIds = new Map(marketId, selectionIds)
    // return (map) -> market id,  array of selection ids

    const marketSelectionIds = new Map<string, string[]>();
    // Get unique market IDs from bets
    const uniqueMarketIds = [...new Set(betModels.map((bet) => bet.market_id))];

    for (const marketId of uniqueMarketIds) {
      // Get all runners from market data
      const market = this.betsMarkets$.getValue()[marketId];
      const runners = market?.runners || [];

      // Use all market runners as selections
      const allSelections = runners.map((runner) => runner?.selectionId?.toString());
      marketSelectionIds.set(marketId, allSelections);
      // console.log(`🔍 Market ${marketId}: ${allSelections.length} total runners from market data:`, allSelections);
    }
    return marketSelectionIds;
  }

  evaluateExposureValidation(
    nonPendingBets: CalculationBet[],
    balanceData: BalanceData,
    predictedBet: PredictedBet,
    currentBetId: string,
    shouldDebug: boolean = true,
  ):
    | {
        insufficientBalance: boolean;
      }
    | {
        cancelUnmatchedBetsFirst: boolean;
      }
    | null {
    const debuggingKey =
      `${this.betsMarkets$.getValue()[predictedBet.marketId]?.event?.name} - ${this.betsMarkets$.getValue()[predictedBet.marketId]?.marketName} - ${currentBetId}`
        .toUpperCase()
        .includes("UNDEFINED")
        ? undefined
        : `${this.betsMarkets$.getValue()[predictedBet.marketId]?.event?.name} - ${this.betsMarkets$.getValue()[predictedBet.marketId]?.marketName} - ${currentBetId}`.toUpperCase();

    const oddsExposuresStepOne: Record<string, number> = {}; // Will hold exposure values for ODDS based markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
    const asianHandicapExposuresStepOne: Record<string, number> = {}; // Will hold exposure values for AsianHandicap based markets. Key is market ID
    const goalLineExposuresStepOne: Record<string, number> = {}; // Will hold exposure values for Goal-Line based markets. Key is market ID
    const doubleChanceExposuresStepOne: Record<string, number> = {}; // Will hold exposure values for ODDS based markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
    const lineExposuresStepOne: Record<string, number> = {}; // Will hold exposure values for LINE based markets. Key is <marketId>-<selectionId>

    // Get list of the user's open bets excluding the bet that is being updated (this applies only when we call this function during bet update)
    const nonPendingBetsWithoutCurrentBet = nonPendingBets.filter((bet) => bet.betId !== currentBetId);

    // Separate ODDS and LINE bets
    const oddsBets: CalculationBet[] = [];
    const asianHandicapBets: CalculationBet[] = [];
    const goalLineBets: CalculationBet[] = [];
    const doubleChanceBets: CalculationBet[] = [];
    const lineBets: CalculationBet[] = [];

    nonPendingBetsWithoutCurrentBet.forEach((bet) => {
      if (this.isAsianHandicapBet(bet)) {
        asianHandicapBets.push(bet);
      } else if (this.isGoalLineBet(bet)) {
        goalLineBets.push(bet);
      } else if (this.isLineBet(bet)) {
        lineBets.push(bet);
      } else if (this.isDoubleChanceBet(bet)) {
        doubleChanceBets.push(bet);
      } else {
        oddsBets.push(bet);
      }
    });

    // Insert the predicted bet in the appropriate group
    if (this.isAsianHandicapBet(predictedBet)) {
      asianHandicapBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    } else if (this.isGoalLineBet(predictedBet)) {
      goalLineBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    } else if (this.isLineBet(predictedBet)) {
      lineBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    } else if (this.isDoubleChanceBet(predictedBet)) {
      doubleChanceBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    } else {
      oddsBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    }

    // const oddsBetsExcludeUnmatched = [...oddsBets].filter((bet) => {
    //   // const unmatchedBets =
    //   // console.log("unmatchedBets: ", unmatchedBets);
    //
    //   // // composite key for the current bet
    //   // const currentKey = `${bet.marketId}-${bet.selectionId}-${bet.side}`;
    //   //
    //   // // Check if there is any unmatched bet with the same composite key
    //   // return !unmatchedBets.some(unmatchedBet => {
    //   //   const unmatchedKey = `${unmatchedBet.marketId}-${unmatchedBet.selectionId}-${unmatchedBet.side}`;
    //   //   return unmatchedKey === currentKey;
    //   // });
    //
    //   const isMatched = this.unmatchedBets.some(unmatchedBet => unmatchedBet.betId === bet.betId);
    //
    //
    //   return !isMatched;
    // });

    // Logging to verify the results
    // console.log("oddsBetsExcludeUnmatched: ", oddsBetsExcludeUnmatched.length);
    // console.log("Total oddsBets: ", oddsBets);
    // console.log("Unmatched Bets: ", this.unmatchedBets.length);

    // Get unique IDs from the bets
    const oddsMarketsMarketGroupIds = Array.from(new Set(oddsBets.map((bet) => bet.marketGroupId)));
    const asianHandicapMarketsMarketGroupIds = Array.from(new Set(asianHandicapBets.map((bet) => bet.marketGroupId)));
    const goalLineMarketsMarketGroupIds = Array.from(new Set(goalLineBets.map((bet) => bet.marketGroupId)));
    const doubleChanceMarketsMarketGroupIds = Array.from(new Set(doubleChanceBets.map((bet) => bet.marketGroupId)));
    const lineMarketsUniqueIds = Array.from(new Set(lineBets.map((bet) => `${bet.marketId}-${bet.selectionId}`)));

    // For each unique IDs, calculate and store the exposure result
    oddsMarketsMarketGroupIds.forEach((marketGroupId) => (oddsExposuresStepOne[marketGroupId] = this.calculateMarketBasedExposure(oddsBets, marketGroupId)));
    asianHandicapMarketsMarketGroupIds.forEach(
      (marketGroupId) => (asianHandicapExposuresStepOne[marketGroupId] = this.calculateAsianHandicapBasedExposure(asianHandicapBets, marketGroupId)),
    );
    goalLineMarketsMarketGroupIds.forEach(
      (marketGroupId) => (goalLineExposuresStepOne[marketGroupId] = this.calculateGoalLineBasedExposure(goalLineBets, marketGroupId)),
    );
    doubleChanceMarketsMarketGroupIds.forEach(
      (marketGroupId) =>
        (doubleChanceExposuresStepOne[marketGroupId] = this.calculateDoubleChanceBasedExposure(
          doubleChanceBets.filter((bet) => bet.marketGroupId === marketGroupId),
        )),
    );
    lineMarketsUniqueIds.forEach((uniqueId) => (lineExposuresStepOne[uniqueId] = this.calculateRunnerBasedExposure(lineBets, uniqueId)));

    // Sum all the exposures got from the step above (This will be the total exposure)
    const oddsTotalExposure = Object.values(oddsExposuresStepOne).reduce((prev, curr) => prev + curr, 0);
    const asianHandicapTotalExposure = Object.values(asianHandicapExposuresStepOne).reduce((prev, curr) => prev + curr, 0);
    const goalLineTotalExposure = Object.values(goalLineExposuresStepOne).reduce((prev, curr) => prev + curr, 0);
    const doubleChanceTotalExposure = Object.values(doubleChanceExposuresStepOne).reduce((prev, curr) => prev + curr, 0);
    const lineTotalExposure = Object.values(lineExposuresStepOne).reduce((prev, curr) => prev + curr, 0);

    const unMatchedBetsExposure: number = this.unmatchedNonPendingBets$.getValue().reduce((acc, bet) => {
      // console.log("potential: ",bet.potentialLoss)
      return acc + +bet.potentialLoss;
    }, 0);

    const newTotalExposure = oddsTotalExposure + lineTotalExposure + doubleChanceTotalExposure + asianHandicapTotalExposure + goalLineTotalExposure;
    const potentialLoss = this.enhancePredictedBetToCalculationBet(predictedBet).potentialLoss;
    const exposureWhenUnmatched = Math.abs(+balanceData.TOTAL_EXPOSURE);
    const maxExposure = predictedBet.side === "LAY" ? Math.max(newTotalExposure, exposureWhenUnmatched) : newTotalExposure;
    // console.log("Max: ",newTotalExposure, exposureWhenUnmatched, potentialLoss, maxExposure)

    console.log("newTotalExposure: ", newTotalExposure);

    let worstExposure = 0;

    try {
      const allBetsWithPredictBet = [...nonPendingBetsWithoutCurrentBet, this.enhancePredictedBetToCalculationBet(predictedBet)];
      const doubleChanceBets = allBetsWithPredictBet.filter((bet) => this.isDoubleChanceBet(bet));
      // const asianHandicapBets = allBetsWithPredictBet.filter(bet => this.isAsianHandicap(bet))
      const goalLineBets = allBetsWithPredictBet.filter((bet) => this.isGoalLineBet(bet));

      const matchedOddBets = allBetsWithPredictBet.filter((bet) => {
        return !this.isDoubleChanceBet(bet) && !this.isAsianHandicapBet(bet) && !this.isGoalLineBet(bet) && !this.isLineBet(bet);
      });

      const allBets = matchedOddBets.map((bet) => ({
        betId: bet.betId,
        marketId: bet.marketId,
        selectionId: bet.selectionId?.toString(),
        side: bet.side?.toLowerCase() as "back" | "lay",
        price: bet.betId === "-1" ? Number(bet.fOdds) : Number(bet.averagePriceMatched) || Number(bet.fOdds) || 1.01,
        betSize: Number(bet.betSize),
        stakeMatched: Number(bet.stakeMatched),
      }));

      // @ts-ignore
      let betModels = allBets.map((betDto) => RiskBet.fromDto(betDto));
      const marketSelectionIds: Map<string, string[]> = this.getMarketSelectionIds(betModels);
      worstExposure = this.riskExposureService.calculateSumOfIndividualMarketExposures(betModels, marketSelectionIds);

      worstExposure += doubleChanceTotalExposure + asianHandicapTotalExposure + goalLineTotalExposure + lineTotalExposure;
      // console.log('🔵 Current logic exposure:', maxExposure);
      // console.log('🟢 Risk-exposure-js WORST exposure:', worstExposure);
      // console.log('📊 Difference:', Math.abs(maxExposure - worstExposure));
    } catch (error) {
      console.error("❌ Risk exposure calculation error:", error);
    }

    if (shouldDebug && debuggingKey) {
      let newMarketExposure;
      if (this.isAsianHandicapBet(predictedBet)) {
        newMarketExposure = asianHandicapExposuresStepOne[predictedBet.marketGroupId];
      } else if (this.isGoalLineBet(predictedBet)) {
        newMarketExposure = goalLineExposuresStepOne[predictedBet.marketGroupId];
      } else if (this.isLineBet(predictedBet)) {
        newMarketExposure = lineExposuresStepOne[`${predictedBet.marketId}-${predictedBet.selectionId}`];
      } else if (this.isDoubleChanceBet(predictedBet)) {
        newMarketExposure = doubleChanceExposuresStepOne[predictedBet.marketGroupId];
      } else {
        newMarketExposure = oddsExposuresStepOne[predictedBet.marketGroupId];
      }
      this.debuggingService.exposureDebuggingObject.next({
        ...this.debuggingService.exposureDebuggingObject.getValue(),
        [debuggingKey]: {
          newTotalExposure: worstExposure,
          newMarketExposure,
          exposureWhenUnmatched,
        },
      });
    }

    // Step 0
    // NEW LOGIC: Check unmatched LAY bets for this market and compare exposures
    const unmatchedBetsForMarket = this.unmatchedNonPendingBets$
      .getValue()
      .filter((bet) => this.generateMarketGroupIdForCalculationBet(bet) === predictedBet.marketGroupId);

    // const unmatchedExposureForMarket = unmatchedBetsForMarket.reduce((acc, bet) => {
    //     // console.log("potential: ",bet.potentialLoss)
    //     return  acc + +bet.potentialLoss
    //   },0);

    // Count #Lay unmatched bets for this market
    // const nMarketUnMatchedLayBets = unmatchedBetsForMarket.filter(bet => bet.side === 'LAY').length;
    const nMarketUnMatchedBets = unmatchedBetsForMarket.length;
    // const unMatchedBetsExposure: number = this.unmatchedNonPendingBets$.getValue().reduce((acc, bet) => {
    //   // console.log("potential: ",bet.potentialLoss)
    //   return  acc + +bet.potentialLoss
    // },0);

    // Calculate this market's exposure without the predicted bet
    // Get all bets for this market, then remove the predicted bet (which was just added)
    const betsForThisMarket = oddsBets.filter((bet) => bet.marketGroupId === predictedBet.marketGroupId);
    const betsWithoutPredicted = betsForThisMarket.slice(0, -1); // Remove last added (predicted bet)
    //
    const oldMarketExposure = this.calculateMarketBasedExposure(betsWithoutPredicted, predictedBet.marketGroupId);
    // console.log("Old Market Exposure: ", oldMarketExposure)
    //
    // // Calculate this market's exposure with predicted bet (current oddsBets already includes it)
    const newMarketExposure = this.calculateMarketBasedExposure(betsForThisMarket, predictedBet.marketGroupId);
    // console.log("New Market Exposure: ", newMarketExposure)
    // const totalExposure =  +balanceData.TOTAL_EXPOSURE - +balanceData.LIABILITY;

    // if (nMarketUnMatchedLayBets === 0) {
    //   if (Math.abs(newTotalExposure) <= +balanceData.BALANCE) {
    //     console.log("----- step -1 ------", newTotalExposure, +balanceData.BALANCE );
    //     return null;
    //   }
    // }
    // all bets regardless of unmatched LAY bets in the market
    // if (nMarketUnMatchedLayBets !== -1) {

    // Debugger log
    // console.log("newMarketExposure: ",newMarketExposure)
    // console.log("oldMarketExposure: ",oldMarketExposure)
    // console.log("newTotalExposure: ", newTotalExposure)

    // const potentialLoss = this.enhancePredictedBetToCalculationBet(predictedBet).potentialLoss;
    // console.log("potentalLoss: ", potentialLoss)

    // console.log("first condition: ", nMarketUnMatchedBets);
    // Prevents total exposure from exceeding balance
    // if(nMarketUnMatchedBets !== 0) {
    //   // console.log("inside first condition: ", newTotalExposure, unMatchedBetsExposure, balanceData.BALANCE);
    //   if ((Math.abs(newTotalExposure) + unMatchedBetsExposure) > +balanceData.BALANCE) {
    //     // console.log("----- step 0 ------")
    //     return { insufficientBalance: true };
    //   }else {
    //     // console.log("inside first condition [else]: ", potentialLoss, totalExposure)
    //     if((+potentialLoss + -totalExposure) > +balanceData.BALANCE) {
    //       // console.log("----- step 0.1 -----")
    //       return  {insufficientBalance: true}
    //     }else {
    //      return null
    //     }
    //   }
    // }else {
    //   if((+potentialLoss + -totalExposure) > +balanceData.BALANCE) {
    //     // console.log("----- step 0.1 -----")
    //     return  {insufficientBalance: true}
    //   }else {
    //     return null
    //   }
    // }

    /**
     * If Predicted bet is Lay bet && unmatchedBackBet > 0
     *   const consideredMatchedBet = assume all unmatched back bet as matched bet + all matched bet
     *   const worstExposure = calculateExposure(allUnmatchedLayBet + predictedBet, consideredMatchedBet)
     *   if(worstExposure > balance) {
     *       {insufficientBalance: true}
     *   }
     *
     */
    // Get all matched and unmatched bets
    const allMatchedBets = this.matchedNonPendingBets$.getValue();
    const allUnmatchedBets = this.unmatchedNonPendingBets$.getValue();

    // Convert to CalculationBet format
    const matchedCalculationBets = allMatchedBets.map((bet) => this.enhanceBetToCalculationBet(bet, bet.stakeMatched));
    const unmatchedCalculationBets = allUnmatchedBets.map((bet) => this.enhanceBetToCalculationBet(bet, bet.betSize));

    // Separate unmatched bets by side
    const unmatchedBackBets = unmatchedCalculationBets.filter((bet) => bet.side === "BACK");
    const unmatchedLayBets = unmatchedCalculationBets.filter((bet) => bet.side === "LAY");

    if (predictedBet.side === "LAY" && unmatchedBackBets.length > 0) {
      const consideredMatchedBets = [...matchedCalculationBets, ...unmatchedBackBets];
      const worstCaseUnmatchedBets = [...unmatchedLayBets, this.enhancePredictedBetToCalculationBet(predictedBet)];

      if (this.checkWorstCaseExposure(worstCaseUnmatchedBets, consideredMatchedBets, balanceData)) {
        console.log("step 0.2");
        return { insufficientBalance: true };
      }
    }

    /**
     * If Predicted bet is Back bet && unmatchedLayBet > 0
     *   const consideredMatchedBet = assume all unmatched lay bet as matched bet + all matched bet
     *   const worstExposure = calculateExposure(allUnmatchedBackBet + predictedBet, consideredMatchedBet)
     *   if(worstExposure > balance) {
     *       {insufficientBalance: true}
     *   }
     *
     */
    if (predictedBet.side === "BACK" && unmatchedLayBets.length > 0) {
      const consideredMatchedBets = [...matchedCalculationBets, ...unmatchedLayBets];
      const worstCaseUnmatchedBets = [...unmatchedBackBets, this.enhancePredictedBetToCalculationBet(predictedBet)];

      if (this.checkWorstCaseExposure(worstCaseUnmatchedBets, consideredMatchedBets, balanceData)) {
        console.log("step 0.3");
        return { insufficientBalance: true };
      }
    }

    // if(oldMarketExposure < newMarketExposure) {
    //   const avlToBet = +balanceData.BALANCE + +balanceData.TOTAL_EXPOSURE
    //   // console.log("Available to bet: ", avlToBet, potentialLoss)
    //
    //   if(avlToBet < +potentialLoss) {
    //     console.log("step 0.4")
    //     return  {insufficientBalance: true}
    //   }
    // }

    console.log(
      "[worstExposure]: ",
      worstExposure,
      " | [exposureWhenUnmatched]: ",
      exposureWhenUnmatched,
      " | [newTotalExposure]: ",
      newTotalExposure,
      " | [balance]: ",
      balanceData.BALANCE,
    );
    let cancelUnmatchedBet = false;
    if (newMarketExposure < oldMarketExposure) {
      if (unmatchedBetsForMarket.length === 0) {
        // if ((Math.abs(maxExposure)) > +balanceData.BALANCE) {
        //   return {insufficientBalance: true}
        // }
        return null;
      }
      cancelUnmatchedBet = true;
    }

    // if(+balanceData.BALANCE <= exposureWhenUnmatched){
    //   console.log("----- step 0.0 ------")
    //     return {insufficientBalance: true}
    //   }

    if (Math.abs(worstExposure) > +balanceData.BALANCE) {
      console.log("----- step 0 ------");
      if (cancelUnmatchedBet) {
        return { insufficientBalance: true };
      }
      return { insufficientBalance: true };
    } else {
      console.log("----- step 0.1 ------");
      if (worstExposure > +balanceData.BALANCE) {
        return { insufficientBalance: true };
      }
      return null;
    }

    //  if(nMarketUnMatchedBets !== 0) {
    // }

    // if (Math.abs(newTotalExposure) >= +balanceData.BALANCE) {
    //   console.log("----- step 1 ------")
    //   return {insufficientBalance: true}
    // }else {
    //   console.log("----- step 1.1 ------")
    //   return null;
    // }

    // console.log("second condition: ", newTotalExposure, balanceData.BALANCE);
    //   if (Math.abs(newTotalExposure) >= +balanceData.BALANCE) {
    //     console.log("----- step -1 ------", newTotalExposure, +balanceData.BALANCE );
    //     return {insufficientBalance: true}
    //   }else {
    //     return null;
    //   }
    // console.log(totalExposure)

    // step 0.1
    // if((+potentialLoss + -totalExposure) > +balanceData.BALANCE) {
    //   // console.log("----- step 0.1 -----")
    //   return  {insufficientBalance: true}
    // }else {
    //  return null
    // }
    // }

    // ORIGINAL LOGIC (kept as reference):
    // Calculate total exposure with the predicted bet. If the total exposure(from the balance) > calculated total exposure, return null.
    // sum-up all potential loss for all unmatched bet for predicted market (Back)
    // how many unmatched Lay bet their for the predicted market

    // Calculate unmatched bets across all markets
    // const unmatchedBetsForMarket = this.unmatchedNonPendingBets$.getValue().filter( bet => this.generateMarketGroupIdForCalculationBet(bet) === predictedBet.marketGroupId);
    // const allUnmatchedBets = this.unmatchedNonPendingBets$.getValue();

    // Sum of potential losses from all unmatched BACK bets across all markets
    // let unmatchedBackExposure = allUnmatchedBets
    //   .filter(bet => bet.side === 'BACK' || bet.side === 'LAY')
    //   .reduce((sum, bet) => sum + Number(bet.potentialLoss || bet.betSize), 0);

    // Count of unmatched LAY bets across all markets
    // let noUnmatchedLayBets = unmatchedBetsForMarket
    //   .filter(bet => bet.side === 'LAY').length;

    // const totalAvlBet = +balanceData.TOTAL_EXPOSURE + +balanceData.LIABILITY + +balanceData.BALANCE;
    // console.log("Avl. Bet:", totalAvlBet, Math.abs(+balanceData.TOTAL_EXPOSURE) + totalAvlBet);
    // console.log("New Exposure: ", newTotalExposure)
    // console.log(+balanceData.TOTAL_EXPOSURE + +balanceData.LIABILITY + +balanceData.BALANCE)
    // if(noUnmatchedLayBets === 0 && predictedBet) {
    //     if ((Math.abs(Number(balanceData.TOTAL_EXPOSURE)))  > newTotalExposure + unmatchedBackExposure) {
    // 	    return null
    //     }
    // }
    // if(Math.abs(+balanceData.BALANCE) > newTotalExposure) {
    //   return null
    // }else {
    //   return  {insufficientBalance: true}
    // }
    // else {
    // return {insufficientBalance: true}
    // }

    // // Step 1
    // // Check if the bet is a LAY bet on a BF ODDS market and if the available balance is less than the market exposure
    // if (predictedBet.side === "LAY" && predictedBet.provider === MarketProvider.BF && predictedBet.bettingType !== "LINE") {
    //   const oddsBetsExposure = this.calculateMarketBasedExposure(oddsBets, predictedBet.marketGroupId);
    //   const doubleChanceExposure = this.calculateDoubleChanceBasedExposure(doubleChanceBets);
    //   const marketExposure = oddsBetsExposure + doubleChanceExposure;
    //
    //   const availableToBet = this.getAvailableToBet(balanceData);
    //
    //   if (availableToBet < marketExposure) {
    //     console.log("step 1")
    //     return { insufficientBalance: true };
    //   }
    // }

    // // Step 2
    // // If validation fails, return the error
    // const validation = this.exposureValidation(newTotalExposure, balanceData);
    // if (validation) {
    //   return validation;
    // }

    // // Step 3
    // const oddsExposuresStepThree: Record<string, number> = {}; // Will hold exposure values for ODDS based markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
    // const asianHandicapExposuresStepThree: Record<string, number> = {}; // Will hold exposure values for AsianHandicap based markets. Key is market ID
    // const goalLineExposuresStepThree: Record<string, number> = {}; // Will hold exposure values for Goal-Line based markets. Key is market ID
    // const doubleChanceExposuresStepThree: Record<string, number> = {}; // Will hold exposure values for double-chance markets. Key is unique ID (marketId for odds markets and topic for bookmaker markets)
    // const lineExposuresStepThree: Record<string, number> = {}; // Will hold exposure values for LINE based markets. Key is <marketId>-<selectionId>
    // const matchedBets: CalculationBet[] = [];
    // const unmatchedBets: CalculationBet[] = [];

    // // Divide partially matched bets into their matched and unmatched parts
    // this.partiallyMatchedBets$.getValue().forEach((bet) => {
    //   const split = this.splitPartiallyMatchedBet(bet);
    //   matchedBets.push(split[0]);
    //   unmatchedBets.push(split[1]);
    // });

    // // Collect matched bets
    // matchedBets.push(
    //   ...this.matchedBets$
    //     .getValue()
    //     .filter((bet) => bet.betId !== currentBetId)
    //     .map((bet) => ({
    //       ...bet,
    //       marketGroupId: this.generateMarketGroupIdForCalculationBet(bet),
    //       dfOdds: bet.provider === MarketProvider.BF && bet.bettingType === "LINE" ? bet.averagePriceMatched : bet.dfOdds,
    //       rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
    //     })),
    // );

    // // Collect unmatched bets
    // unmatchedBets.push(
    //   ...this.unmatchedBets$
    //     .getValue()
    //     .filter((bet) => bet.betId !== currentBetId)
    //     .map((bet) => ({ ...bet, marketGroupId: this.generateMarketGroupIdForCalculationBet(bet), rangeOdd: bet.averagePriceMatched ?? bet.dfOdds })),
    // );

    // // Consider lapsed bets as unmatched bets
    // unmatchedBets.push(
    //   ...this.lapsedNonPendingBets$
    //     .getValue()
    //     .filter((bet) => bet.betId !== currentBetId)
    //     .map((bet) => ({
    //       ...bet,
    //       marketGroupId: this.generateMarketGroupIdForCalculationBet(bet),
    //       rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
    //     })),
    // );
    // // Separate ODDS and LINE matched markets
    // const oddsMatchedBets: CalculationBet[] = [];
    // const asianHandicapMatchedBets: CalculationBet[] = [];
    // const goalLineMatchedBets: CalculationBet[] = [];
    // const doubleChanceMatchedBets: CalculationBet[] = [];
    // const lineMatchedBets: CalculationBet[] = [];

    // matchedBets.forEach((bet) => {
    //   if (this.isAsianHandicapBet(bet)) {
    //     asianHandicapMatchedBets.push(bet);
    //   } else if (this.isGoalLineBet(bet)) {
    //     goalLineMatchedBets.push(bet);
    //   } else if (this.isLineBet(bet)) {
    //     lineMatchedBets.push(bet);
    //   } else if (this.isDoubleChanceBet(bet)) {
    //     doubleChanceMatchedBets.push(bet);
    //   } else {
    //     oddsMatchedBets.push(bet);
    //   }
    // });

    // // Insert the predicted bet in the appropriate group
    // if (this.isAsianHandicapBet(predictedBet)) {
    //   asianHandicapMatchedBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    // } else if (this.isGoalLineBet(predictedBet)) {
    //   goalLineMatchedBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    // } else if (this.isLineBet(predictedBet)) {
    //   lineMatchedBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    // } else if (this.isDoubleChanceBet(predictedBet)) {
    //   doubleChanceMatchedBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    // } else {
    //   oddsMatchedBets.push(this.enhancePredictedBetToCalculationBet(predictedBet));
    // }

    // // Get unique market IDs and predictedBet's market ID from the bets
    // const matchedOddsBetsMarketGroupIds = Array.from(new Set(oddsMatchedBets.map((bet) => bet.marketGroupId)));
    // const matchedAsianHandicapBetsMarketGroupIds = Array.from(new Set(asianHandicapMatchedBets.map((bet) => bet.marketGroupId)));
    // const matchedGoalLineBetsMarketGroupIds = Array.from(new Set(goalLineMatchedBets.map((bet) => bet.marketGroupId)));
    // const doubleChanceBetsMarketGroupIds = Array.from(new Set(doubleChanceMatchedBets.map((bet) => bet.marketGroupId)));
    // const matchedLineBetsUniqueIds = Array.from(new Set(lineMatchedBets.map((bet) => `${bet.marketId}-${bet.selectionId}`)));

    // matchedOddsBetsMarketGroupIds.forEach(
    //   (marketGroupId) => (oddsExposuresStepThree[marketGroupId] = this.calculateMarketBasedExposure(oddsMatchedBets, marketGroupId)),
    // );
    // matchedAsianHandicapBetsMarketGroupIds.forEach(
    //   (marketGroupId) => (asianHandicapExposuresStepThree[marketGroupId] = this.calculateAsianHandicapBasedExposure(asianHandicapMatchedBets, marketGroupId)),
    // );
    // matchedGoalLineBetsMarketGroupIds.forEach(
    //   (marketGroupId) => (goalLineExposuresStepThree[marketGroupId] = this.calculateGoalLineBasedExposure(goalLineMatchedBets, marketGroupId)),
    // );
    // doubleChanceBetsMarketGroupIds.forEach(
    //   (marketGroupId) => (doubleChanceExposuresStepThree[marketGroupId] = this.calculateDoubleChanceBasedExposure(doubleChanceMatchedBets)),
    // );
    // matchedLineBetsUniqueIds.forEach((uniqueId) => (lineExposuresStepThree[uniqueId] = this.calculateRunnerBasedExposure(lineMatchedBets, uniqueId)));

    // // Sum all the exposures got from the step above (This will be the total exposure)
    // const matchedOddsBetsTotalExposure = Object.values(oddsExposuresStepThree).reduce((prev, curr) => prev + curr, 0);
    // const matchedAsianHandicapBetsTotalExposure = Object.values(asianHandicapExposuresStepThree).reduce((prev, curr) => prev + curr, 0);
    // const matchedGoalLineBetsTotalExposure = Object.values(goalLineExposuresStepThree).reduce((prev, curr) => prev + curr, 0);
    // const matchedDoubleChanceBetsTotalExposure = Object.values(doubleChanceExposuresStepThree).reduce((prev, curr) => prev + curr, 0);
    // const matchedLineBetsTotalExposure = Object.values(lineExposuresStepThree).reduce((prev, curr) => prev + curr, 0);
    // const matchedBetsTotalExposure =
    //   matchedOddsBetsTotalExposure +
    //   matchedDoubleChanceBetsTotalExposure +
    //   matchedLineBetsTotalExposure +
    //   matchedAsianHandicapBetsTotalExposure +
    //   matchedGoalLineBetsTotalExposure +
    //   this.calculateUnmatchedExposure(unmatchedBets);

    // if (shouldDebug && debuggingKey) {
    //   let newMarketExposure;
    //   if (this.isAsianHandicapBet(predictedBet)) {
    //     newMarketExposure = asianHandicapExposuresStepOne[predictedBet.marketGroupId];
    //   } else if (this.isGoalLineBet(predictedBet)) {
    //     newMarketExposure = goalLineExposuresStepOne[predictedBet.marketGroupId];
    //   } else if (this.isLineBet(predictedBet)) {
    //     newMarketExposure = lineExposuresStepThree[`${predictedBet.marketId}-${predictedBet.selectionId}`];
    //   } else if (this.isDoubleChanceBet(predictedBet)) {
    //     newMarketExposure = doubleChanceExposuresStepThree[predictedBet.marketGroupId];
    //   } else {
    //     newMarketExposure = oddsExposuresStepThree[predictedBet.marketGroupId];
    //   }
    //   this.debuggingService.exposureDebuggingObject.next({
    //     ...this.debuggingService.exposureDebuggingObject.getValue(),
    //     [debuggingKey]: {
    //       newTotalExposure: matchedBetsTotalExposure,
    //       newMarketExposure,
    //     },
    //   });
    // }

    // // If validation fails, return the error
    // const matchedBetValidation = this.exposureValidation(matchedBetsTotalExposure, balanceData);
    // if (matchedBetValidation) {
    //   return matchedBetValidation;
    // }

    return null;
  }

  calculateDoubleChanceBasedExposure(bets: CalculationBet[]) {
    let home = 0;
    let draw = 0;
    let away = 0;

    //TODO: check if the cases are using the correct value
    for (const bet of bets) {
      switch (bet.selectionId) {
        case 6384648:
          if (bet.side === "BACK") {
            home += Number(bet.potentialProfit);
            draw -= Number(bet.betSize);
            away += Number(bet.potentialProfit);
          } else {
            // LAY
            home -= Number(bet.potentialLoss);
            draw += Number(bet.betSize);
            away -= Number(bet.potentialLoss);
          }
          break;
        case 6384647:
          if (bet.side === "BACK") {
            home -= Number(bet.betSize);
            draw += Number(bet.potentialProfit);
            away += Number(bet.potentialProfit);
          } else {
            // LAY
            home += Number(bet.betSize);
            draw -= Number(bet.potentialLoss);
            away -= Number(bet.potentialLoss);
          }
          break;
        case 6384646:
          if (bet.side === "BACK") {
            home += Number(bet.potentialProfit);
            draw += Number(bet.potentialProfit);
            away -= Number(bet.betSize);
          } else {
            // LAY
            home -= Number(bet.potentialLoss);
            draw -= Number(bet.potentialLoss);
            away += Number(bet.betSize);
          }
          break;
        default:
          break;
      }
    }

    return Math.abs(Math.min(0, home, draw, away));
  }

  exposureValidation(totalExposure: number, balanceData: BalanceData) {
    // If balance is less than totalExposure show insufficient balance validation error
    return Number(balanceData.BALANCE) < totalExposure ? { insufficientBalance: true } : null;
  }

  enhancePredictedBetToCalculationBet(predictedBet: PredictedBet): CalculationBet {
    return {
      marketGroupId: predictedBet.marketGroupId,
      marketId: predictedBet.marketId,
      marketName: predictedBet.marketName,
      selectionId: predictedBet.selectionId,
      side: predictedBet.side,
      potentialProfit: this.calculatePotentialProfit(
        Number(predictedBet.stake),
        Number(predictedBet.fOdds),
        predictedBet.side,
        0,
        Number(predictedBet.fOdds),
        predictedBet.bettingType,
      ),
      potentialLoss: this.calculatePotentialLoss(
        Number(predictedBet.stake),
        Number(predictedBet.fOdds),
        predictedBet.side,
        0,
        Number(predictedBet.fOdds),
        predictedBet.bettingType,
      ),
      fOdds: predictedBet.fOdds ?? 0,
      dfOdds: predictedBet.dfOdds ?? 0,
      rangeOdd: predictedBet.rangeOdd ?? predictedBet.dfOdds ?? 0,
      betSize: predictedBet.stake ?? 0,
      betId: "-1",
      bettingType: predictedBet.bettingType,
      provider: predictedBet.provider,
      handicap: predictedBet.handicap,
      handicapValues: predictedBet.handicapValues,
      averagePriceMatched: predictedBet.averagePriceMatched,
      runnerName: predictedBet.runnerName,
      stakeMatched: 0,
    };
  }

  calculateUnmatchedExposure(bets: CalculationBet[]) {
    return bets.reduce((prev, curr) => prev + Number(curr.potentialLoss), 0);
  }

  splitPartiallyMatchedBet(bet: Bet) {
    return [
      {
        ...bet,
        marketGroupId: this.generateMarketGroupIdForCalculationBet(bet),
        stakeRemaining: 0,
        potentialProfit: this.calculatePotentialProfit(
          Number(bet.stakeMatched),
          Number(bet.fOdds),
          bet.side,
          bet.sizeCancelled,
          Number(bet.fOdds),
          bet.bettingType,
        ),
        potentialLoss: this.calculatePotentialLoss(Number(bet.stakeMatched), Number(bet.fOdds), bet.side, 0, Number(bet.fOdds), bet.bettingType),
        rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
      }, // Matched bet
      {
        ...bet,
        marketGroupId: this.generateMarketGroupIdForCalculationBet(bet),
        stakeMatched: 0,
        potentialProfit: this.calculatePotentialProfit(
          Number(bet.stakeRemaining),
          Number(bet.fOdds),
          bet.side,
          bet.sizeCancelled,
          Number(bet.fOdds),
          bet.bettingType,
        ),
        potentialLoss: this.calculatePotentialLoss(Number(bet.stakeRemaining), Number(bet.fOdds), bet.side, 0, Number(bet.fOdds), bet.bettingType),
        rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
      }, // Unmatched bet
    ];
  }

  /**
   * Converts a bookmaker market to a betfair compatible market object
   * @param bookmakerMarket
   */
  convertBookmakerMarketToBetfairMarket(bookmakerMarket: BookmakerMarket): Market {
    return {
      marketId: bookmakerMarket.marketId,
      marketName: bookmakerMarket.name,
      marketStartTime: "",
      inPlay: true,
      runners: bookmakerMarket.runners.map((runner) => this.convertBookmakerRunnerToBetfairRunner(runner)),
      eventType: bookmakerMarket.eventType,
      competition: bookmakerMarket.competition,
      event: {
        id: bookmakerMarket.eventId,
        name: bookmakerMarket.eventName,
        countryCode: null,
        openDate: null,
        timezone: null,
        venue: null,
      },
      totalMatched: 0,
      description: {
        bettingType: bookmakerMarket.bettingType,
      },
    };
  }

  convertBookmakerRunnerToBetfairRunner(bookmakerRunner: BookmakerRunner): Runner {
    return {
      selectionId: bookmakerRunner.selectionId,
      runnerName: bookmakerRunner.runnerName,
      handicap: bookmakerRunner.handicap,
      sortPriority: bookmakerRunner.sort,
      metadata: {},
      status: bookmakerRunner.status,
    };
  }

  /**
   * Converts a fancy market to a betfair compatible market object
   * @param fancyMarket
   */
  convertFancyMarketToBetfairMarket(fancyMarket: FancyMarket): Market {
    return {
      marketId: fancyMarket.marketId,
      marketName: fancyMarket.eventName,
      marketStartTime: "",
      inPlay: true,
      runners: fancyMarket.runners.map((runner) => this.convertFancyRunnerToBetfairRunner(runner)),
      eventType: fancyMarket.eventType,
      competition: fancyMarket.competition,
      event: {
        id: fancyMarket.eventId,
        name: fancyMarket.eventName,
        countryCode: null,
        openDate: null,
        timezone: null,
        venue: null,
      },
      totalMatched: 0,
      description: {
        bettingType: fancyMarket.bettingType,
      },
    };
  }

  convertFancyRunnerToBetfairRunner(fancyRunner: FancyRunner): Runner {
    return {
      selectionId: Number(fancyRunner.selectionId),
      runnerName: fancyRunner.runnerName,
      handicap: fancyRunner.handicap,
      sortPriority: fancyRunner.priority,
      metadata: {},
      status: fancyRunner.status,
    };
  }

  getDetailsForMultipleSbMarkets(keys: string) {
    // getDetailsForMultipleSbMarkets(keys: { providerName: string; eventId: string; providerId: string }[]) {
    return this.httpClient
      .get<MarketResponse<(BookmakerMarket | FancyMarket)[]>>(`${MARKET_URL}/sb/markets`, {
        params: {
          ids: keys,
        },
      })
      .pipe(map((response) => response.data));
  }

  /**
   * Get the F_ODDS value
   * @param odd
   * @param provider
   * @param isLineMarket
   */
  getFinalOdds(odd: number, provider: MarketProvider, isLineMarket: boolean) {
    if (provider === MarketProvider.BF && isLineMarket) {
      return 2;
    }
    if (provider === MarketProvider.SB) {
      return odd / 100 + 1;
    }

    return odd;
  }

  generateMarketGroupIdForCalculationBet({
    marketId,
    selectionId,
    provider,
    bettingType,
    topic,
  }: Pick<Bet, "marketId" | "provider" | "bettingType" | "selectionId" | "topic">) {
    if (provider === MarketProvider.BF && bettingType !== "LINE") {
      // ODDS market
      return marketId;
    } else if (provider === MarketProvider.SB && bettingType !== "LINE") {
      // ODDS market
      return topic ?? `${marketId}-${selectionId}`;
    } else {
      // BF LINE, Fancy & Bookmaker market
      return `${marketId}-${selectionId}`;
    }
  }

  /**
   * Perform periodic memory cleanup
   */
  private performMemoryCleanup() {
    if (typeof window !== "undefined" && window.gc) {
      try {
        window.gc();
      } catch (error) {
        // Silently fail if gc is not available
      }
    }
    console.log("Memory cleanup performed");
  }

  private collectBetMarkets() {
    // Prepare market details for non-pending betfair bets
    this.nonPendingBets$
      .pipe(
        map((bets) => bets.filter((bet) => bet.provider === MarketProvider.BF)), // Filter only betfair bets
        filter((bets) => bets.length > 0),
        map((bets) => Array.from(new Set(bets.map((bet) => bet.marketId)))), // Make the IDs list be only of unique IDs
        switchMap((marketIds) => this.getDetailsForMultipleNonPendingBfMarkets(marketIds)),
      )
      .subscribe({
        next: (betsMarkets) => {
          const currentBetMarkets = this.betsMarkets$.getValue();

          if (Array.isArray(betsMarkets)) {
            const tempUnmatchedBetsMarkets: Record<string, Market> = {};
            betsMarkets.forEach((market) => {
              tempUnmatchedBetsMarkets[market.marketId] = market;
            });
            this.betsMarkets$.next({ ...currentBetMarkets, ...tempUnmatchedBetsMarkets });
          } else {
            this.betsMarkets$.next({ ...currentBetMarkets, [betsMarkets.marketId]: betsMarkets });
          }
        },
      });
    // Prepare market details for pending betfair bets
    this.pendingBets$
      .pipe(
        map((bets) => bets.filter((bet) => bet.provider === MarketProvider.BF)), // Filter only betfair bets
        filter((bets) => bets.length > 0),
        map((bets) => Array.from(new Set(bets.map((bet) => bet.marketId)))), // Make the IDs list be only of unique IDs
        switchMap((marketIds) => this.getDetailsFoPendingBfMarkets(marketIds)),
      )
      .subscribe({
        next: (betsMarkets) => {
          const currentBetMarkets = this.betsMarkets$.getValue();

          const tempUnmatchedBetsMarkets: Record<string, Market> = {};
          betsMarkets.forEach((market) => {
            tempUnmatchedBetsMarkets[market.marketId] = market;
          });
          this.betsMarkets$.next({ ...currentBetMarkets, ...tempUnmatchedBetsMarkets });
        },
      });

    // Prepare market details for SB bets
    this.betPlacementService.openBetsSubject
      .pipe(
        map((bets: Bet[]) => bets.filter((bet) => bet.provider === MarketProvider.SB)), // Filter only SB bets
        filter((bets) => bets.length > 0),
        map((bets) => Array.from(new Set(bets.map((key) => `${key.descriptionPrefix}_${key.eventId}_${key.providerId}`))).join(",")), // Make the IDs list be only of unique IDs
        switchMap((marketIds) => this.getDetailsForMultipleSbMarkets(marketIds)),
      )
      .subscribe({
        next: (markets) => {
          const currentBetMarkets = this.betsMarkets$.getValue();
          const tempUnmatchedBetsMarkets: Record<string, Market> = {};

          markets.forEach((market) => {
            if (market.descriptionPrefix === "Bookmaker") {
              tempUnmatchedBetsMarkets[market.marketId] = this.convertBookmakerMarketToBetfairMarket(market as BookmakerMarket);
            } else if (market.descriptionPrefix === "Fancy") {
              tempUnmatchedBetsMarkets[market.marketId] = this.convertFancyMarketToBetfairMarket(market as FancyMarket);
            }
          });

          this.betsMarkets$.next({ ...currentBetMarkets, ...tempUnmatchedBetsMarkets });
        },
      });
  }

  private calculateLadderRangesAndValues(bets: { fOdds: number; rangeOdd: number; side: Side; stake: number }[]): { ranges: string[]; values: number[] } {
    if (bets.length > 0) {
      // This will contain both already placed bets plus bet to be placed(if any).
      const ranges: string[] = []; // Ranges to be returned for the table to render
      const values: number[] = []; // Values to be returned for the table to render

      // Sort the bets in ascending order
      const sortedCombinedBets = bets.sort((a, b) => a.rangeOdd - b.rangeOdd);

      const minDisplayOdd = sortedCombinedBets[0]?.rangeOdd; // The minimum displayOdd in the list of displayOdds
      const maxDisplayOdd = sortedCombinedBets[sortedCombinedBets.length - 1].rangeOdd; // The maximum displayOdd in the list of displayOdds

      for (let currentOdd = Math.max(0, minDisplayOdd - LADDER_RANGE_LIMIT); currentOdd < maxDisplayOdd + (LADDER_RANGE_LIMIT + 1); currentOdd += 1) {
        ranges.push(Math.floor(currentOdd)?.toString());
        values.push(
          this.generatePredictedRangeValue(
            sortedCombinedBets,
            Math.floor(currentOdd),
            Math.floor(currentOdd) === currentOdd ? MarketProvider.SB : MarketProvider.BF,
          ),
        );
      }

      return {
        ranges,
        values,
      };
    }

    return {
      ranges: [],
      values: [],
    };
  }

  private generatePredictedRangeValue(
    bets: { fOdds: number; rangeOdd: number; side: Side; stake: number }[],
    flooredCurrentOdd: number,
    provider: MarketProvider,
  ) {
    return bets
      .map((bet) => {
        if (
          (provider === MarketProvider.BF && bet.side === "BACK" && flooredCurrentOdd <= bet.rangeOdd) ||
          (bet.side === "LAY" && flooredCurrentOdd >= bet.rangeOdd) ||
          (provider === MarketProvider.SB && bet.side === "BACK" && flooredCurrentOdd < bet.rangeOdd) ||
          (bet.side === "LAY" && flooredCurrentOdd >= bet.rangeOdd)
        ) {
          return bet.side === "BACK" ? bet.stake : bet.stake * (Number(bet.fOdds) - 1);
        }

        return -(bet.side === "BACK" ? bet.stake * (Number(bet.fOdds) - 1) : bet.stake);
      })
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0);
  }

  isAsianHandicapBet(bet: Pick<Partial<Bet>, "bettingType" | "marketName">) {
    return bet.bettingType && new RegExp(/ASIAN_HANDICAP/, "ig").test(bet.bettingType) && bet.marketName && bet.marketName === "Asian Handicap";
  }

  isGoalLineBet(bet: Pick<Partial<Bet>, "bettingType" | "marketName">) {
    return bet.bettingType && new RegExp(/ASIAN_HANDICAP/, "ig").test(bet.bettingType) && bet.marketName && bet.marketName === "Goal Lines";
  }

  isLineBet(bet: Pick<Partial<Bet>, "bettingType">) {
    return bet.bettingType === "LINE";
  }

  isDoubleChanceBet(bet: Pick<Partial<Bet>, "marketName">) {
    return bet.marketName === "Double Chance";
  }

  calculateAsianHandicapBasedExposure(bets: CalculationBet[], marketGroupId: string): number {
    const betsOnlyWithinMarket = this.getBetsOnlyWithinMarket(bets, marketGroupId);

    const market = this.betsMarkets$.getValue()[betsOnlyWithinMarket[0]?.marketId];

    if (market && market.runners) {
      const marketPnLs = this.asianHandicapService.determineACHPnL(market, betsOnlyWithinMarket);

      const marketExposures: number[] = marketPnLs.map((exposure) => exposure.pnls?.map((pnl: { pnl: number }) => pnl.pnl).flat()).flat();

      return Math.abs(Math.min(...marketExposures, 0));
    }

    return 0;
  }

  calculateGoalLineBasedExposure(bets: CalculationBet[], marketGroupId: string): number {
    const betsOnlyWithinMarket = this.getBetsOnlyWithinMarket(bets, marketGroupId);

    const market = this.betsMarkets$.getValue()[betsOnlyWithinMarket[0]?.marketId];

    if (market && market.runners) {
      const marketPnLs = this.asianHandicapService.determineGLPnL(market, betsOnlyWithinMarket);

      const marketExposures: number[] = marketPnLs.map((exposure) => exposure.pnls?.map((pnl: { pnl: number }) => pnl.pnl).flat()).flat();

      return Math.abs(Math.min(...marketExposures, 0));
    }

    return 0;
  }

  private checkWorstCaseExposure(worstCaseUnmatchedBets: CalculationBet[], consideredMatchedBets: CalculationBet[], balanceData: BalanceData): boolean {
    const allBetsForRiskService = [
      ...consideredMatchedBets.map((bet) => ({
        betId: bet.betId,
        marketId: bet.marketId,
        selectionId: bet.selectionId?.toString(),
        side: bet.side?.toLowerCase() as "back" | "lay",
        price: bet.betId === "-1" ? Number(bet.fOdds) : Number(bet.averagePriceMatched) || Number(bet.fOdds) || 1.01,
        betSize: Number(bet.betSize),
        stakeMatched: Number(bet.stakeMatched),
      })),
      ...worstCaseUnmatchedBets.map((bet) => ({
        betId: bet.betId,
        marketId: bet.marketId,
        selectionId: bet.selectionId?.toString(),
        side: bet.side?.toLowerCase() as "back" | "lay",
        price: bet.betId === "-1" ? Number(bet.fOdds) : Number(bet.averagePriceMatched) || Number(bet.fOdds) || 1.01,
        betSize: Number(bet.betSize),
        stakeMatched: 0,
      })),
    ];

    const marketSelectionIds: Map<string, string[]> = this.getMarketSelectionIds(allBetsForRiskService);
    const worstExposure = this.riskExposureService.calculateExposure(allBetsForRiskService, marketSelectionIds);
    return worstExposure > +balanceData.BALANCE;
  }

  private enhanceBetToCalculationBet(bet: Bet, betSize: string | number): CalculationBet {
    return {
      ...bet,
      marketGroupId: this.generateMarketGroupIdForCalculationBet(bet),
      potentialProfit: bet.potentialProfit,
      potentialLoss: bet.potentialLoss,
      betSize: betSize,
      averagePriceMatched: bet.averagePriceMatched,
      rangeOdd: bet.averagePriceMatched ?? bet.dfOdds,
    };
  }
}
